<template>
    <header class="sticky top-0 z-50 lg:relative">
        <div class="w-full bg-neutral-content">
            <div class="w-full max-w-screen-3xl m-auto flex justify-start items-center ps-2 py-1 pe-6 lg:justify-center min-h-8 relative">
                <div
                    v-if="data"
                    ref="staticLinksContainer"
                    class="flex max-w-[255px] md:max-w-none justify-start items-center lg:justify-center leading-6 overflow-x-scroll md:overflow-x-hidden"
                    @scroll="checkStaticLinksScrollPosition"
                >
                    <template v-for="(menuItem, index) in staticLinks" :key="index">
                        <template v-if="menuItem.isTargetExtern">
                            <a
                                v-if="hasContent(menuItem.url) && hasContent(menuItem.title)"
                                :href="menuItem.url"
                                :target="menuItem.newTab ? '_blank' : '_self'"
                                rel="noreferrer"
                                class="text-xxs text-secondary-content flex items-center leading-4 gap-1.5 px-3 py-2 whitespace-nowrap border-r border-base-content-subtle"
                            >
                                <MiscIcon v-if="menuItem.icon" :name="menuItem.icon" size="sm" />
                                {{ menuItem.title }}
                            </a>
                        </template>
                        <template v-else>
                            <MiscLink
                                v-if="hasContent(menuItem.url) && hasContent(menuItem.title)"
                                :to="menuItem.url"
                                :target="menuItem.newTab ? '_blank' : null"
                                class="text-xxs text-secondary-content flex items-center leading-4 gap-1.5 px-3 py-2 whitespace-nowrap border-r border-base-content-subtle"
                            >
                                <MiscIcon v-if="menuItem.icon" :name="menuItem.icon" size="sm" />
                                {{ menuItem.title }}
                            </MiscLink>
                        </template>
                    </template>
                </div>
                
                <!-- Mobile scroll indicator arrow -->
                <div v-if="data && staticLinks && staticLinks.length > 0 && !staticLinksScrolledToTheEnd" class="absolute left-[255px] top-0 bottom-0 flex items-center justify-end pointer-events-none md:hidden bg-neutral-content px-2">
                    <MiscIcon name="chevron-right" class="text-secondary-content" />
                </div>

                <LayoutTopBarCountry
                    class="ml-auto cursor-pointer"
                    :aria-label="$t('layout.header.showLanguageSwitch')"
                    @click="showLanguageSwitch = !showLanguageSwitch"
                />
            </div>
        </div>

        <div
            v-if="showLanguageSwitch"
            class="language-dropdown-content overflow-y-show min-w-[304px] flex flex-col gap-3 p-5 bg-base-100 z-[51] border-b absolute inset-x-0 sm:inset-x-auto top-[28px] mx-2 sm:mx-0 sm:right-4 shadow rounded-box border"
        >
            <client-only>
                <LayoutLanguageSwitch />
            </client-only>
        </div>

        <div class="bg-base-100 border-b">
            <section
                class="m-auto flex items-center relative px-[10px] py-[3px] z-40 max-w-screen-3xl
                    lg:p-0 lg:flex-row lg:flex-wrap lg:justify-between lg:items-stretch"
            >
                <div
                    tabindex="0"
                    class="relative flex flex-col items-center mr-[10px] lg:hidden"
                    aria-haspopup="menu"
                    role="button"
                    :aria-label="$t('layout.header.menu')"
                    :aria-expanded="sidenav.isOpen('menu').value"
                    @click="sidenav.toggle('menu')"
                    @keydown.enter.prevent="sidenav.toggle('menu')"
                >
                    <div class="btn btn-ghost btn-circle">
                        <MiscIcon name="menu" />
                    </div>
                    <span class="text-xs hidden lg:inline-block">{{ $t('layout.header.menu') }}</span>
                </div>

                <div class="lg:pt-4 lg:pb-4 lg:order-4">
                    <LayoutLogo size="header" />
                </div>

                <div class="hidden self-center items-center lg:flex lg:self-auto lg:order-2 lg:pl-6 lg:flex-1" />

                <div
                    class="items-center ml-auto flex
                    lg:relative lg:bottom-auto lg:m-0 lg:order-6 lg:gap-4 lg:p-0 lg:rounded-none lg:border-0 lg:bg-transparent lg:pr-6 lg:flex-1 lg:justify-end"
                >
                    <LayoutHeaderActions />
                </div>
            </section>
        </div>

        <div class="hidden w-full border-b lg:bg-base-200 lg:block relative">
            <LayoutNavigationHorizontal v-if="navigation" :navigation="navigation" />
        </div>
    </header>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

type HeaderConfig = Schemas['CmsElementA4gHeader']['config']

const props = defineProps<{
    navigation?: Schemas['NavigationRouteResponse']
    data: HeaderConfig
}>()

const staticLinks = computed(() => {
    return props.data?.staticLinks?.value
})

const staticLinksScrolledToTheEnd = ref(false)
const staticLinksContainer = ref<HTMLElement>()

function hasContent (input: string | null) {
    return (input != null && input.trim() != '')
}

function checkStaticLinksScrollPosition() {
    if (!staticLinksContainer.value) return
    const { scrollLeft, scrollWidth, clientWidth } = staticLinksContainer.value
    staticLinksScrolledToTheEnd.value = scrollLeft >= scrollWidth - clientWidth - 1
}

const showLanguageSwitch = ref(false)

const sidenav = useSidenav()

useRuntimeHook('app:layout:header:openLanguageSwitch', () => {
    showLanguageSwitch.value = true
})

onMounted(() => {
    nextTick(() => {
        checkStaticLinksScrollPosition()
    })
})
</script>
